import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, Calendar as CalendarIcon, Sparkles, Timer } from "lucide-react";
import { format } from "date-fns";
import type { ReactNode } from "react";

type ClientOption = { id: string; name: string; email: string };

/**
 * DBWorkoutBuilder header (sticky).
 *
 * Notes:
 * - Keep *layout* concerns (sticky, spacing, responsive wrapping) here.
 * - Keep *state and side-effects* (saving meta, toggling mode) in DBWorkoutBuilder and pass callbacks in.
 */
export default function DBWorkoutBuilderHeader({
  name,
  onNameChange,
  durationMinutes,
  onDurationMinutesChange,
  isSaving,
  lastSavedAt,
  backLabel,
  onBack,
  isAssignedMode,
  workoutMode,
  onWorkoutModeChange,
  clientsLoading,
  selectedClientId,
  clientOptions,
  assignmentDate,
  onAssignmentDateChange,
}: {
  name: string;
  onNameChange: (next: string) => void | Promise<void>;
  durationMinutes: number;
  onDurationMinutesChange: (next: number) => void | Promise<void>;
  isSaving: boolean;
  lastSavedAt: Date | null;
  backLabel: string;
  onBack: () => void;
  isAssignedMode: boolean;
  workoutMode: "template" | "assigned";
  onWorkoutModeChange: (v: "template" | "assigned") => void;
  clientsLoading: boolean;
  selectedClientId: string | null;
  clientOptions: ClientOption[];
  assignmentDate: Date | null;
  onAssignmentDateChange: (d: Date | null) => void | Promise<void>;
}) {
  const selectedClientName = selectedClientId
    ? clientOptions.find((c) => c.id === selectedClientId)?.name || "Unknown Client"
    : null;

  return (
    <div className="sticky top-0 z-20 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b">
      <div className="flex flex-col gap-3 py-3 px-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="hidden md:flex items-center justify-center h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-400 to-lime-400 text-gray-900 shadow-sm">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Input
                value={name}
                onChange={(e) => void onNameChange(e.target.value)}
                className="text-xl font-semibold h-10 bg-white/70 hover:bg-white focus:bg-white transition-colors text-gray-900 border-gray-200 shadow-sm"
              />
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-full border bg-white/70 shadow-sm">
                <Timer className="w-4 h-4 text-gray-500" />
                <Input
                  type="number"
                  value={durationMinutes}
                  onChange={(e) => {
                    const v = parseInt(e.target.value || "0", 10);
                    if (!Number.isNaN(v) && v >= 0) void onDurationMinutesChange(v);
                  }}
                  className="h-7 w-16 border-0 bg-transparent px-0 text-sm"
                  placeholder="min"
                />
                <span className="text-xs text-gray-600">min</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center text-xs text-gray-600 px-2">
              {isSaving ? (
                <span className="font-medium text-amber-700">Saving…</span>
              ) : (
                <span>
                  Last saved:{" "}
                  <span className="font-medium text-gray-800">
                    {lastSavedAt ? format(lastSavedAt, "PP p") : "—"}
                  </span>
                </span>
              )}
            </div>
            <Button variant="outline" onClick={onBack} className="gap-2">
              <ArrowLeft className="w-4 h-4" /> {backLabel}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {!isAssignedMode ? (
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-gray-700">Mode:</Label>
              <ToggleGroup
                type="single"
                value={workoutMode}
                onValueChange={(v) => v && onWorkoutModeChange(v as "template" | "assigned")}
                className="bg-gray-100 rounded-md p-1"
              >
                <ToggleGroupItem
                  value="template"
                  className="px-3 py-1 text-sm font-medium rounded-md border data-[state=on]:bg-primary data-[state=on]:text-black data-[state=on]:border-primary data-[state=off]:bg-white data-[state=off]:text-gray-700 data-[state=off]:border-transparent data-[state=off]:hover:bg-gray-200"
                >
                  Template
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          ) : null}

          {isAssignedMode ? (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium text-gray-700">Client:</Label>
                {clientsLoading ? (
                  <span className="text-sm text-gray-500">Loading...</span>
                ) : selectedClientName ? (
                  <span className="text-sm font-medium text-gray-900 bg-white/70 px-3 py-1.5 rounded-md border border-gray-200">
                    {selectedClientName}
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">No client selected</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Label htmlFor="date-picker" className="text-sm font-medium text-gray-700">
                  Date:
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="date-picker"
                      variant="outline"
                      className="w-[200px] justify-start text-left font-normal bg-white/70"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {assignmentDate ? format(assignmentDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-gray-200 shadow-lg" align="start">
                    <Calendar
                      mode="single"
                      selected={assignmentDate || undefined}
                      onSelect={(date) => void onAssignmentDateChange(date || null)}
                      initialFocus
                      className="bg-white text-gray-900"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

